/**
 * The stacking moves that put artboards into a new list order WITHOUT moving them past
 * any loose art (QA 2026-09-24, artboards-F12).
 *
 * Tidy Up and a reorder in the Artboards list used to bring every board to the front of
 * the page, even when the order had not changed, so a loose drawing hanging partly over a
 * page had the overlapping part hidden behind the page's white background. Now the boards
 * swap among the slots boards already hold: board `sequence[k]` takes the k-th board slot
 * in `children` (back to front), and everything that is not a board stays where it is.
 *
 * Answers the moves to make, back to front, each placing one board just behind the thing
 * that should follow it — or at the front when nothing does. No moves when the order
 * already reads that way.
 */
export function boardSlotMoves(
  children: readonly string[],
  isBoard: (id: string) => boolean,
  sequence: readonly string[],
): { id: string; position: 'front' | { before: string } }[] {
  const target = [...children];
  let k = 0;
  for (let i = 0; i < target.length && k < sequence.length; i++) {
    if (isBoard(target[i]!)) target[i] = sequence[k++]!;
  }
  if (target.every((id, i) => id === children[i])) return [];
  const moves: { id: string; position: 'front' | { before: string } }[] = [];
  // From the front backwards: each board goes just behind its successor, which is either
  // loose art (never moved) or a board already placed. Nothing is later put between the
  // two, so the whole list ends up reading as `target`.
  for (let i = target.length - 1; i >= 0; i--) {
    const id = target[i]!;
    if (!isBoard(id)) continue;
    moves.push({ id, position: i === target.length - 1 ? 'front' : { before: target[i + 1]! } });
  }
  return moves;
}
